// ./src/utils/gloveboxHelpers.ts
// Shared utility helpers for Glovebox components (selectors, lists, cards, etc.)
// Provides consistent icon and color mappings for document categories and types.

// ============================================
// Category Icon Mapping
// ============================================

const CATEGORY_ICONS: Record<string, string> = {
  vehicle: 'Car',
  driver: 'User',
  equipment: 'Tool',
  other: 'File',
};

// ============================================
// Category Color Mapping
// ============================================

const CATEGORY_COLORS: Record<string, string> = {
  vehicle: 'blue',
  driver: 'green',
  equipment: 'orange',
  other: 'gray',
};

// ============================================
// Doc Type Code → Icon Mapping
// Maps specific document type codes to more descriptive icons.
// Falls back to category icon if code is not mapped.
// ============================================

const DOC_TYPE_ICONS: Record<string, string> = {
  // Vehicle documents
  REGISTRATION: 'FileText',
  INSURANCE_POLICY: 'Shield',
  INSURANCE_CARD: 'Shield',
  TITLE: 'Award',
  SAFETY_INSPECTION: 'ClipboardCheck',
  EMISSIONS_TEST: 'Wind',
  WARRANTY: 'ShieldCheck',
  EXTENDED_WARRANTY: 'ShieldCheck',
  PURCHASE_AGREEMENT: 'Signature',
  BILL_OF_SALE: 'Receipt',
  LOAN_AGREEMENT: 'Building',
  LEASE_AGREEMENT: 'FileText',
  ROADSIDE_ASSISTANCE: 'Phone',
  PARKING_PERMIT: 'ParkingCircle',
  TOLL_TRANSPONDER: 'Radio',
  IMPORT_DOCUMENT: 'Package',
  RECALL_NOTICE: 'AlertTriangle',
  VIGNETTE: 'Sticker',
  ENVIRONMENTAL_STICKER: 'Leaf',

  // Driver documents
  DRIVERS_LICENSE: 'CreditCard',
  INTERNATIONAL_PERMIT: 'Globe',
  COMMERCIAL_LICENSE: 'Truck',
  MOTORCYCLE_ENDORSEMENT: 'Bike',
  MEDICAL_CERTIFICATE: 'HeartRateMonitor',
  DEFENSIVE_DRIVING_CERT: 'Award',
  HAZMAT_ENDORSEMENT: 'AlertOctagon',
  TAXI_LICENSE: 'Car',

  // Equipment
  FIRST_AID_KIT: 'Heart',
  FIRE_EXTINGUISHER: 'Flame',
  WARNING_TRIANGLE: 'AlertTriangle',
  REFLECTIVE_VEST: 'Eye',
  BREATHALYZER: 'GlassFull',
  TOW_ROPE: 'Link',
  SPARE_TIRE: 'Circle',
  JUMPER_CABLES: 'BatteryCharging',
  ICE_SCRAPER: 'Snowflake',
  TIRE_CHAINS: 'GripHorizontal',
  DASH_CAM: 'Camera',
  CAR_JACK: 'ArrowUp',
  FLASHLIGHT: 'Bulb',
  EMERGENCY_BLANKET: 'Shirt',
  PORTABLE_AIR_COMPRESSOR: 'Gauge',

  // Other
  CUSTOM: 'File',
};

// ============================================
// Public API
// ============================================

/**
 * Get the icon name for a glovebox document category.
 * @param category - The document category (vehicle, driver, equipment, other)
 * @returns Icon name string compatible with AppIcon / lucide-react
 */
export const getCategoryIcon = (category: string | undefined | null): string => {
  if (!category) return 'File';
  return CATEGORY_ICONS[category] || 'File';
};

/**
 * Get the color for a glovebox document category.
 * @param category - The document category (vehicle, driver, equipment, other)
 * @returns Mantine color string
 */
export const getCategoryColor = (category: string | undefined | null): string => {
  if (!category) return 'gray';
  return CATEGORY_COLORS[category] || 'gray';
};

/**
 * Get the icon name for a specific glovebox document type code.
 * Falls back to the category icon if no specific mapping exists.
 * @param code - The document type code (e.g., 'FIRE_EXTINGUISHER', 'INSURANCE_POLICY')
 * @param category - Optional fallback category for the icon
 * @returns Icon name string compatible with AppIcon / lucide-react
 */
export const getDocTypeIcon = (
  code: string | undefined | null,
  category?: string | undefined | null
): string => {
  if (code && DOC_TYPE_ICONS[code]) {
    return DOC_TYPE_ICONS[code];
  }
  return getCategoryIcon(category);
};